'use client';

import type {
  CategoriesEntity,
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { HeaderCartButton } from '@eliadmin/core/src/components/header/header-cart-button';
import { HeaderMenu } from '@eliadmin/core/src/components/header/header-menu';
import { HeaderProfileButton } from '@eliadmin/core/src/components/header/header-profile-button';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';
import { SearchButton } from '@eliadmin/core/src/components/header/search-button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Logo } from '../ui/logo';
import { HeaderComparisionButton } from './header-comparision-button';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  whiteText: boolean;
  categories?: CategoriesEntity[];
  mobileSidebarCategories?: CategoriesEntity[];
  topbarMenu?: Menu;
};

/**
 * Figma's header is a single row — logo left, category nav centred, actions right — instead of
 * the previous two-row layout (search bar + logo + actions, with the nav on its own line below).
 * The desktop search input is replaced by the icon that opens the full-screen search sheet, which
 * `SearchButton` already provides.
 */
export function HeaderMain({
  generalLayout,
  whiteText,
  categories = [],
  mobileSidebarCategories = [],
  topbarMenu,
}: HeaderMainProps) {
  const logoSrc = whiteText ? generalLayout?.footer?.logo?.url : generalLayout?.header?.logo?.url;

  return (
    <MobileSearchProvider>
      <div className={cn('custom-container', 'hidden items-center justify-between gap-6 lg:flex')}>
        <Logo className={{ image: 'h-12' }} generalLayout={generalLayout} src={logoSrc} />
        <HeaderMenu categories={categories} whiteText={whiteText} />
        <div className="flex items-center justify-end gap-6">
          <SearchButton whiteText={whiteText} className={{ icon: 'h-[18px] w-[18px]' }} />
          <HeaderProfileButton whiteText={whiteText} />
          <HeaderCartButton whiteText={whiteText} />
        </div>
      </div>

      {/* Below `lg` the nav collapses into the existing mobile sidebar. */}
      <div className={cn('custom-container', 'flex items-center justify-between gap-3 lg:hidden')}>
        <div className="flex flex-1 items-center justify-start">
          <MobileSidebar
            side="left"
            whiteText={whiteText}
            categories={mobileSidebarCategories}
            menu={topbarMenu?.fields}
          />
        </div>
        <Logo className={{ image: 'h-8 sm:h-10' }} generalLayout={generalLayout} src={logoSrc} />
        <div className="flex flex-1 items-center justify-end gap-3">
          <HeaderComparisionButton whiteText={whiteText} />
          <div className={cn('h-6 w-[1px]', whiteText ? 'bg-white/25' : 'bg-black/15')} />
          <SearchButton whiteText={whiteText} />
          {/* The bottom bar is `sm:hidden`, so between 640 and 1023px profile and cart must
              live here — otherwise tablets have no way to reach the cart or sign in. */}
          <div className="hidden items-center gap-3 sm:flex">
            <HeaderProfileButton whiteText={whiteText} />
            <HeaderCartButton whiteText={whiteText} />
          </div>
        </div>
      </div>
    </MobileSearchProvider>
  );
}
